import axios from 'axios';

const clearAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('refresh_token');
  try { sessionStorage.removeItem('tenant_features'); } catch {}
};

const handleUnauthorized = () => {
  clearAuth();
  localStorage.setItem('auth_expired', '1');
  window.location.href = '/login';
};

// 401 z verejných auth endpointov (zlé heslo pri logine a pod.) NIE JE vypršané
// prihlásenie — bez tejto výnimky každé zlé heslo reloadlo /login s hláškou
// „Platnosť prihlásenia vypršala" a skutočná chyba sa nikdy nezobrazila.
const isPublicAuthEndpoint = (url?: string) =>
  !!url && /\/auth\/(login|register|set-password|refresh|logout)/.test(url);

// Tichá obnova prihlásenia: krátky JWT vypršal, ale máme 30-dňový refresh
// token. Single-flight — viac súbežných 401 spustí len JEDNO volanie
// /auth/refresh (server token rotuje, druhé volanie by už neprešlo).
let refreshPromise: Promise<boolean> | null = null;
export const tryRefresh = (): Promise<boolean> => {
  if (refreshPromise) return refreshPromise;
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return Promise.resolve(false);
  refreshPromise = axios.post('/api/auth/refresh', { refreshToken })
    .then(r => {
      localStorage.setItem('token', r.data.token);
      localStorage.setItem('user', JSON.stringify(r.data.user));
      localStorage.setItem('refresh_token', r.data.refreshToken);
      return true;
    })
    .catch(() => {
      // Neplatný/odvolaný refresh token — zahoď ho, nech sa oň nepokúšame dokola.
      localStorage.removeItem('refresh_token');
      return false;
    })
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
};

// Odhlásenie (spoločné pre všetky stránky): odvolaj refresh token na serveri
// a vyčisti storage. fetch s keepalive prežije aj okamžitý redirect na /login.
export const logout = () => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (refreshToken) {
    try {
      fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* best-effort */ }
  }
  clearAuth();
  window.location.href = '/login';
};

const api = axios.create({ baseURL: '/api' });

api.interceptors.request.use(config => {
  // Explicitne zadanú hlavičku NEPREPISUJEME. Mobilný zber (/capture) posiela
  // mesačný capture token; predtým ho interceptor prebil denným 'token'om,
  // takže po jeho vypršaní šiel na server neplatný token → 401 → odhlásenie,
  // hoci capture token ešte žil.
  if (config.headers.Authorization) return config;
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// 401 → najprv skús potichu obnoviť prihlásenie refresh tokenom a request
// zopakovať (raz — _retried bráni slučke). Až keď ani to nevyjde, odhlás.
const on401 = async (err: any) => {
  const cfg = err.config;
  if (err.response?.status === 401 && cfg && !isPublicAuthEndpoint(cfg.url)) {
    // Request s capture tokenom (/capture) nerefreshuj ani nepresmerúvaj —
    // jeho chyby si rieši Capture sám (má vlastný re-login flow).
    const capture = localStorage.getItem('capture_token');
    if (capture && cfg.headers?.Authorization === `Bearer ${capture}`) {
      return Promise.reject(err);
    }
    if (!cfg._retried && await tryRefresh()) {
      cfg._retried = true;
      cfg.headers = cfg.headers || {};
      cfg.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      return axios(cfg);
    }
    handleUnauthorized();
  }
  return Promise.reject(err);
};

api.interceptors.response.use(res => res, on401);

// Globálny interceptor pre priame axios volania (napr. SuperAdmin, Login)
axios.interceptors.response.use(res => res, on401);

export default api;
